import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ApiRequestError } from '@/lib/api';
import type { ApiError, ApiSuccess, ResponseMeta } from '@/lib/types';

/**
 * The portal's own HTTP client.
 *
 * Deliberately not the staff client. Different token keys, so a client signing
 * in on a shared machine cannot inherit a staff session and a staff member
 * cannot inherit a client's; different refresh path; and no shared interceptor
 * that might one day send the wrong bearer to the wrong realm.
 */

const ACCESS_KEY = 'oro.portal.access';
const REFRESH_KEY = 'oro.portal.refresh';

export const portalTokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(pair: { accessToken: string; refreshToken: string }): void {
    localStorage.setItem(ACCESS_KEY, pair.accessToken);
    localStorage.setItem(REFRESH_KEY, pair.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const client: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = portalTokens.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshInFlight: Promise<{ accessToken: string; refreshToken: string }> | null = null;

async function refresh() {
  const refreshToken = portalTokens.refresh;
  if (!refreshToken) throw new Error('No session');

  refreshInFlight ??= axios
    .post<ApiSuccess<{ accessToken: string; refreshToken: string }>>(
      `${client.defaults.baseURL}/portal/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((response) => {
      portalTokens.set(response.data.data);
      return response.data.data;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

type Retriable = AxiosRequestConfig & { _retried?: boolean };

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const config = error.config as Retriable | undefined;

    if (
      error.response?.status === 401 &&
      config &&
      !config._retried &&
      !config.url?.includes('/portal/auth/') &&
      portalTokens.refresh
    ) {
      config._retried = true;
      try {
        const pair = await refresh();
        config.headers = { ...config.headers, Authorization: `Bearer ${pair.accessToken}` };
        return client.request(config);
      } catch {
        portalTokens.clear();
        if (!window.location.pathname.startsWith('/portal/login')) {
          window.location.href = '/portal/login';
        }
      }
    }

    if (error.response?.data?.error) {
      throw new ApiRequestError(error.response.status ?? 500, error.response.data);
    }

    throw new ApiRequestError(error.response?.status ?? 0, {
      success: false,
      error: { code: 'INTERNAL_ERROR', message: error.message || 'Could not reach the server' },
      requestId: 'client',
    });
  },
);

export async function portalGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await client.get<ApiSuccess<T>>(url, config);
  return response.data.data;
}

/**
 * A paginated GET.
 *
 * The server's interceptor hoists `{ data, meta }` into the envelope, so a
 * list endpoint's `data` is the rows themselves and `meta` sits beside it.
 * Reading `.data.data` off `portalGet` is how a page ends up calling `.length`
 * on undefined.
 */
export async function portalList<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<{ data: T; meta: ResponseMeta }> {
  const response = await client.get<ApiSuccess<T>>(url, config);
  return {
    data: response.data.data,
    meta: response.data.meta ?? { page: 1, limit: 25, total: 0, totalPages: 1 },
  };
}

export async function portalPost<T>(url: string, body?: unknown): Promise<T> {
  const response = await client.post<ApiSuccess<T>>(url, body);
  return response.data.data;
}
